import React, {
  CSSProperties,
  MutableRefObject,
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { logger } from 'services';
import { debounce } from './util';

interface Props {
  numItems: number;
  itemHeight: number;
  renderItem: (props: { index: number; style: CSSProperties }) => JSX.Element;
  parentHeight: number;
  debounceTimer?: number;
  onScroll?: (start: number, end: number) => void;
  innerRef?: MutableRefObject<any>;
  onMount?: (start: number, end: number) => void;
  name: string;
}

export interface VirtualizedListRef {
  getScrollOffset: () => { start: number; end: number };
}

export const VirtualizedList = forwardRef<VirtualizedListRef, Props>(
  (props, ref) => {
    const {
      numItems,
      itemHeight,
      renderItem,
      parentHeight,
      debounceTimer = 200,
      onScroll: onPropScroll,
      innerRef,
    } = props;
    const [scrollTop, setScrollTop] = useState(0);
    const innerHeight = numItems * itemHeight - 40;
    const startIndex = Math.max(Math.floor(scrollTop / itemHeight) - 2, 0);
    const endIndex = Math.min(
      numItems - 1,
      Math.floor((scrollTop + parentHeight) / itemHeight) + 2,
    );

    useImperativeHandle(ref, () => ({
      getScrollOffset: () => ({
        start: startIndex,
        end: endIndex,
      }),
    }));

    const items: JSX.Element[] = [];

    for (let i = startIndex; i <= endIndex; i += 1) {
      items.push(
        renderItem({
          index: i,
          style: {
            position: 'absolute',
            top: `${i * itemHeight}px`,
          },
        }),
      );
    }

    const propOnScroll = useMemo(
      () =>
        debounce((start, end) => {
          onPropScroll?.(start, end);
        }, debounceTimer),
      [debounceTimer, onPropScroll],
    );

    useLayoutEffect(() => {
      // we need to send to parent how many items should it fetch from background
      // we duplicated start and end because its not inside useState so the latest values won't get picked up
      const start = Math.max(Math.floor(scrollTop / itemHeight) - 2, 0);
      const end = Math.min(
        numItems - 1,
        Math.floor((scrollTop + parentHeight) / itemHeight) + 2,
      );

      props.onMount?.(start, end);
      // we aren't bothered about dynamic itemHeight on initial mount so we can ignore it in deps
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numItems]);

    return (
      <div
        className="scroll"
        style={{
          overflowY: numItems > 1 ? 'auto' : 'hidden',
          height: `${parentHeight}px`,
        }}
        onScroll={(e) => {
          e.stopPropagation();
          setScrollTop(e.currentTarget.scrollTop);
          propOnScroll(startIndex, endIndex);
        }}
        id="scroll"
        ref={innerRef}
        onDragEnd={() => {
          logger.debug('ending drag');
        }}
        onDrop={(e) => {
          e.preventDefault();
        }}
      >
        <div
          className="inner"
          style={{
            position: 'relative',
            height: `${innerHeight}px`,
            padding: '0 8px',
          }}
          onDrop={(e) => {
            e.preventDefault();
          }}
        >
          {items}
        </div>
      </div>
    );
  },
);
